import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { ACTIVITY_TYPES } from 'constants/general';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { TabBodyWrapper } from '../TabBodyWrapper';
import { ActivityType } from 'interfaces/common.interface';
import { isActivityFilterByAll } from 'utils/validators';
import {
  breakActivitiesSelector,
  addNewActivityButtonSelector
} from 'store/reducer/setting/selectors';
import { changeActivityOrder, newHabit } from 'store/reducer/setting/slice';
import { updateFilterIsSettingToolbarShow } from 'store/reducer/filter/slice';
import { getNewHabitProps } from 'utils/helpers';
import RoutineActivitiesWrapperRevamp from './RoutineActivitiesWrapperRevamp';
import DraggableContentRevamp from '../DraggableContentRevamp';
import AddHabitButton from 'components/setting-generator-revamp/tabs-revamp/AddHabitButton';
import { t } from 'services/i18n.service';
import TabBodyHeaderRevamp from './tab-body-header-revamp';
import SkeletonRoutineList from '../skeleton-routine';

export default function BreaksRevamp() {
  const dispatch = useAppDispatch();
  const {
    break_activities,
    filter: { searchWord, daysOfWeek, priority },
    isSettingLoading
  } = useAppSelectorShallow(breakActivitiesSelector);

  const {
    tabs,
    activeTabIndex,
    customRoutine,
    currentSettings,
    routineSuggestionsSelectedRoutines,
    platform
  } = useAppSelectorShallow(addNewActivityButtonSelector);

  const isSettingToolbarShown = useAppSelectorShallow(
    (state) => state.filter.isSettingToolbarShown
  );

  const sequence_id = break_activities?.[0]?.activity_sequence_id ?? uuid();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const visibleActivities = (break_activities ?? []).filter((activity) =>
    isActivityFilterByAll(activity, searchWord, priority, daysOfWeek)
  );

  const visibleItemIds = visibleActivities.map((a) => String(a.id));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      // Compute indices against the full list (not just visible), so reordering is consistent
      const fullIds = (break_activities ?? []).map((a) => String(a.id));
      const oldIndex = fullIds.indexOf(String(active.id));
      const newIndex = fullIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      const reordered = arrayMove(
        Array.from(break_activities ?? []),
        oldIndex,
        newIndex
      );
      dispatch(
        changeActivityOrder({
          type: ACTIVITY_TYPES.BREAKING,
          data: reordered
        })
      );
    },
    [break_activities, dispatch]
  );

  const handleAddHabit = () => {
    const [activity_type, sequence_id] = getNewHabitProps(
      tabs,
      activeTabIndex,
      currentSettings,
      platform,
      customRoutine,
      routineSuggestionsSelectedRoutines
    );
    dispatch(
      newHabit({
        type: activity_type,
        sequence_id
      })
    );
  };

  const handleFilter = () => {
    dispatch(updateFilterIsSettingToolbarShow(!isSettingToolbarShown));
  };

  if (isSettingLoading) return <SkeletonRoutineList />;

  return (
    <TabBodyWrapper>
      <TabBodyHeaderRevamp
        onAddHabit={handleAddHabit}
        onFilter={handleFilter}
      />
      {break_activities?.length ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <RoutineActivitiesWrapperRevamp>
            <SortableContext
              items={visibleItemIds}
              strategy={verticalListSortingStrategy}>
              {break_activities?.map(
                (activity: ActivityType, index: number) => {
                  if (
                    isActivityFilterByAll(
                      activity,
                      searchWord,
                      priority,
                      daysOfWeek
                    )
                  ) {
                    return (
                      <DraggableContentRevamp
                        key={activity.id}
                        id={String(activity.id)}
                        position={index}
                        type={ACTIVITY_TYPES.BREAKING}
                        sequence_id={sequence_id}
                      />
                    );
                  }
                  return null;
                }
              )}
            </SortableContext>
          </RoutineActivitiesWrapperRevamp>
        </DndContext>
      ) : null}
      {!break_activities?.length && (
        <AddHabitButton routine={t('break_activities')} />
      )}
    </TabBodyWrapper>
  );
}
